import React, { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box, Typography, Autocomplete, TextField, Button, IconButton, Tooltip, Checkbox, FormControlLabel,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { presetPartLinks } from './partsUtils';
import {
  addWorkItemPresetPart, updateWorkItemPresetPart, deleteWorkItemPresetPart,
} from '../../actions/apiWorkItemPresetParts';
import TruncatedPartNumber, {
  TruncatedPartDescription,
  PART_NUMBER_MAX_WIDTH,
  partDescriptionMaxWidthSx,
} from './TruncatedPartNumber';

const PresetDefaultParts = ({ preset }) => {
  const dispatch = useDispatch();
  const catalog_parts = useSelector(state => state.settings_refresh?.catalog_parts || []);
  const dealership = useSelector(state => state.settings_dealerships?.installation?.subdomain || state.settings_dealerships?.current_dealership || '');

  const catalogOptions = useMemo(
    () => catalog_parts.filter(p => !p.deleted && (p.active === true || p.active === 'true')),
    [catalog_parts],
  );
  const catalogById = useMemo(
    () => new Map(catalog_parts.map(c => [String(c.id), c])),
    [catalog_parts],
  );

  const links = useMemo(() => presetPartLinks(preset), [preset]);
  const linkedIds = useMemo(() => new Set(links.map(l => String(l.catalog_part_id))), [links]);

  const [selectedPart, setSelectedPart] = useState(null);
  const [qty, setQty] = useState(1);

  const addOptions = useMemo(
    () => catalogOptions.filter(p => !linkedIds.has(String(p.id))),
    [catalogOptions, linkedIds],
  );

  const addPart = () => {
    if (!selectedPart) return;
    dispatch(addWorkItemPresetPart({
      work_item_preset_id: preset.id,
      catalog_part_id: selectedPart.id,
      default_quantity: Number(qty) || 1,
      order: links.length,
      dealership,
    }, 'addWorkItemPresetPart'));
    setSelectedPart(null);
    setQty(1);
  };

  return (
    <Box sx={{
 mt: 2, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1,
}}
    >
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Default Parts</Typography>
      <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1.5 }}>
        Parts added automatically when this preset is used to add a work item.
      </Typography>

      <Box sx={{
 display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 1.5,
}}
      >
        <Autocomplete
          size="small"
          sx={{ width: 320 }}
          options={addOptions}
          value={selectedPart}
          onChange={(e, val) => setSelectedPart(val)}
          getOptionLabel={o => `${o.part_number} — ${o.description || ''}`}
          renderInput={params => <TextField {...params} label="Add a catalog part" />}
        />
        <TextField size="small" type="number" label="Default Qty" sx={{ width: 110 }} value={qty} onChange={e => setQty(e.target.value)} />
        <Button variant="contained" size="small" onClick={addPart} disabled={!selectedPart}>Add</Button>
      </Box>

      {links.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Part #</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Default Qty</TableCell>
              <TableCell align="center">Auto-approve</TableCell>
              <TableCell align="right">Remove</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {links.map((link) => {
              const cat = catalogById.get(String(link.catalog_part_id)) || {};
              return (
                <TableRow key={link.id}>
                  <TableCell sx={{ maxWidth: PART_NUMBER_MAX_WIDTH }}>
                    <TruncatedPartNumber value={cat.part_number || `#${link.catalog_part_id}`} />
                  </TableCell>
                  <TableCell sx={{ maxWidth: partDescriptionMaxWidthSx }}>
                    <TruncatedPartDescription value={cat.description || ''} />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      variant="standard"
                      type="number"
                      defaultValue={link.default_quantity ?? 1}
                      onBlur={(e) => {
                        const value = Number(e.target.value) || 1;
                        if (value !== Number(link.default_quantity)) {
                          dispatch(updateWorkItemPresetPart(link.id, { default_quantity: value }, 'updateWorkItemPresetPart'));
                        }
                      }}
                      sx={{ width: 70 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <FormControlLabel
                      sx={{ m: 0 }}
                      control={(
                        <Checkbox
                          size="small"
                          checked={!!link.auto_approve}
                          onChange={e => dispatch(updateWorkItemPresetPart(link.id, { auto_approve: e.target.checked }, 'updateWorkItemPresetPart'))}
                        />
                      )}
                      label=""
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Remove from preset">
                      <IconButton size="small" color="error" onClick={() => dispatch(deleteWorkItemPresetPart(link.id, 'deleteWorkItemPresetPart'))}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

export default PresetDefaultParts;
